import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUserPickemsForPeriod } from '@/api/query/useLeaderboard';
import { formatPrizeLabel } from '@/utils/prize';
import { getLocalizedField } from '@/utils/localized-field';

/** Aligns with LeaderboardSection expand header: Pickem | Rank | Prize | Points */
export const EXPAND_PICKEMS_GRID =
  'grid grid-cols-[minmax(0,1fr)_46px_56px_56px] gap-x-3 items-center';

interface UserPickemsListProps {
  userId: string;
  period: 'today' | 'week' | 'month' | 'year';
  weekOffset?: number;
  leagueCode?: string;
  /** When true, styles for white “my score” expand panel (dark text). */
  isCurrentUser?: boolean;
}

export const UserPickemsList: React.FC<UserPickemsListProps> = ({
  userId,
  period,
  weekOffset = 0,
  leagueCode,
  isCurrentUser = false,
}) => {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useUserPickemsForPeriod(
    userId,
    period,
    weekOffset,
    leagueCode
  );

  const muted = isCurrentUser ? 'text-background/70' : 'text-muted-foreground';
  const primary = isCurrentUser ? 'text-background' : 'text-white';
  const success = isCurrentUser ? 'text-background' : 'text-success';
  const border = isCurrentUser ? 'border-black/10' : 'border-border/20';
  const empty = isCurrentUser ? 'text-background/40' : 'text-muted-foreground/40';

  if (isLoading) {
    return <div className={`text-sm font-sans ${muted}`}>{t('leaderboard.pickems.loading')}</div>;
  }

  if (error) {
    return <div className={`text-sm font-sans ${muted}`}>{t('leaderboard.pickems.failed')}</div>;
  }

  if (!data?.pickems || data.pickems.length === 0) {
    return <div className={`text-sm font-sans ${muted}`}>{t('leaderboard.pickems.empty')}</div>;
  }

  return (
    <div className="space-y-1">
      {data.pickems.map(pickem => {
        const prizeLabel = formatPrizeLabel(pickem.prize);
        const rank =
          pickem.rank !== null && pickem.rank !== undefined && Number(pickem.rank) > 0
            ? Number(pickem.rank)
            : null;
        const title =
          getLocalizedField(pickem, 'pickem_title', i18n.language) || pickem.pickem_title;

        return (
          <div
            key={pickem.pickem_id}
            className={`${EXPAND_PICKEMS_GRID} text-sm font-sans py-2 border-b ${border} last:border-0`}
          >
            <span className={`truncate ${primary}`}>{title}</span>
            <span className={`text-center ${muted}`}>
              {rank ?? <span className={empty}>-</span>}
            </span>
            <span className={`text-center font-semibold text-xs ${success}`}>
              {prizeLabel ?? <span className={empty}>-</span>}
            </span>
            <span className={`text-center font-medium ${primary}`}>{pickem.points}</span>
          </div>
        );
      })}
    </div>
  );
};
